import {
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon.tsx';
import { AgentMessage } from '@/spaces/chat/messages/AgentMessage.tsx';
import { EditedImageArtifact } from '@/spaces/chat/messages/EditedImageArtifact.tsx';
import { EditInProgressIndicator } from '@/spaces/chat/messages/EditInProgressIndicator.tsx';
import { ExpressConnectionCard } from '@/spaces/chat/messages/ExpressConnectionCard.tsx';
import { InstagramConnectionCard } from '@/spaces/chat/messages/InstagramConnectionCard.tsx';
import { MessageRow } from '@/spaces/chat/messages/MessageRow.tsx';
import { PanelArtifactMessage } from '@/spaces/chat/messages/PanelArtifactMessage.tsx';
import { ReasoningStepList } from '@/spaces/chat/messages/ReasoningStepList.tsx';
import { ReferenceImageRequest as ReferenceImageRequestCard } from '@/spaces/chat/messages/ReferenceImageRequest.tsx';
import { RemovedSlideArtifact } from '@/spaces/chat/messages/RemovedSlideArtifact.tsx';
import { CampaignSetupPanel } from '@/spaces/chat/messages/setup/CampaignSetupPanel.tsx';
import { useSetupIntroReveal } from '@/spaces/chat/messages/setup/useSetupIntroReveal.ts';
import { SocialPostSet } from '@/spaces/chat/messages/SocialPostSet.tsx';
import { SystemMessage } from '@/spaces/chat/messages/SystemMessage.tsx';
import { ThinkingIndicator } from '@/spaces/chat/messages/ThinkingIndicator.tsx';
import { ToolCommentMessage } from '@/spaces/chat/messages/ToolComment.tsx';
import { UserMessage } from '@/spaces/chat/messages/UserMessage.tsx';
import { UserUploadedImage } from '@/spaces/chat/messages/UserUploadedImage.tsx';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

import type {
	MessageWindowItem,
	PanelArtifact,
	ReasoningStep,
	UserUploadedImage as UserUploadedImageMessage,
} from '../../../../bridge/types/messages.ts';

const SCROLL_BOTTOM_THRESHOLD_PX = 80;

// scrollHeight and clientHeight round to whole pixels, while scrollTop keeps its
// fraction on a display with a fractional device pixel ratio, such as Windows at
// 150% scaling. The maximum scrollTop derived from that rounded pair can sit up
// to one pixel above the real maximum, so a view resting at the bottom needs
// this much slack to still read as at the bottom.
const SCROLL_MAX_ROUNDING_PX = 1;

type RenderItem =
	| { kind: 'message'; message: MessageWindowItem }
	| { kind: 'reasoning_group'; id: string; steps: ReasoningStep[] }
	| { kind: 'upload_group'; id: string; images: UserUploadedImageMessage[] }
	| { kind: 'social_post_group'; id: string; messages: PanelArtifact[] };

// The messages that render as a saved post preview. This mirrors the branch in
// PanelArtifactMessage exactly: any other panel_artifact draws a different card
// and must never join a set.
//
// The test is the artifact type ALONE. An inspiration has no panel, so that
// branch is the only way one renders, including an upload whose url is empty by
// definition and a message written before the media fields existed. Requiring a
// url here would leave every uploaded design out of the set, and two uploads in
// one message would stack exactly as they did before this feature.
//
// It returns the message rather than acting as a type predicate on purpose. A
// predicate would narrow the FALSE branch by removing PanelArtifact from the
// union, and a panel_artifact that is not a preview still has to reach the
// branches below.
const asSocialPostPreview = (
	message: MessageWindowItem,
): PanelArtifact | null =>
	message.type === 'panel_artifact' &&
	message.artifact_type === 'inspiration_reference'
		? message
		: null;

// Collapses each run of consecutive reasoning_step messages into one disclosure
// group, so the chat shows a single "thinking" affordance instead of one row
// per step. A panel_artifact or tool_comment between steps does NOT break the
// group — the agent marks the trailing step done, emits the artifact, then
// keeps reasoning, and that whole arc reads as one thought. Any other message
// type (a real agent reply, a user turn) closes the open group. The group is
// pushed to the output by reference at its first step, so steps that arrive
// after an interleaved artifact still join the same disclosure; the artifact
// renders inline after the group. Keyed on the first step's id for stability.
//
// Photos the customer attaches to one message are stored as one
// user_uploaded_image message each, so a run of them is collapsed the same way
// and reads as the single turn it was. Keyed on the first photo's id.
// eslint-disable-next-line react-refresh/only-export-components
export const groupMessages = (messages: MessageWindowItem[]): RenderItem[] => {
	const out: RenderItem[] = [];
	let group: {
		kind: 'reasoning_group';
		id: string;
		steps: ReasoningStep[];
	} | null = null;
	let uploads: {
		kind: 'upload_group';
		id: string;
		images: UserUploadedImageMessage[];
	} | null = null;
	// A run of adjacent saved-post previews. One turn saves each post with its
	// own action, so several links in one message arrive as several adjacent
	// messages, and the customer should get one set to move through instead of
	// one full player per link. Keyed on the first member's id, matching the
	// reasoning group, so the key holds as later members arrive.
	let pending: PanelArtifact[] = [];

	const flushPending = () => {
		if (pending.length === 0) return;
		// A run of one stays exactly what it was, so a single shared post renders
		// through its own message row and the set never appears around it.
		if (pending.length === 1) {
			out.push({ kind: 'message', message: pending[0] });
		} else {
			out.push({
				kind: 'social_post_group',
				id: pending[0].id,
				messages: pending,
			});
		}
		pending = [];
	};

	for (const message of messages) {
		if (message.type === 'user_uploaded_image') {
			// A photo is not a preview, so it ends the preview run, and it must be
			// drained first to keep the thread order.
			flushPending();
			group = null;
			if (uploads) {
				uploads.images.push(message);
			} else {
				uploads = { kind: 'upload_group', id: message.id, images: [message] };
				out.push(uploads);
			}
			continue;
		}
		uploads = null;

		const preview = asSocialPostPreview(message);
		if (preview) {
			pending.push(preview);
			continue;
		}
		// Anything else ends the run and keeps its own place in the thread.
		// Draining the run must NEVER close an open reasoning group: the two are
		// separate, and closing one here would split a disclosure that a
		// panel_artifact is meant to pass through.
		flushPending();

		if (message.type === 'reasoning_step') {
			if (group) {
				group.steps.push(message);
			} else {
				group = { kind: 'reasoning_group', id: message.id, steps: [message] };
				out.push(group);
			}
			continue;
		}
		if (message.type === 'panel_artifact' || message.type === 'tool_comment') {
			out.push({ kind: 'message', message });
			continue;
		}
		group = null;
		out.push({ kind: 'message', message });
	}
	// The window commonly ends mid-turn, while the rest of the run is still
	// arriving.
	flushPending();

	return out;
};

// eslint-disable-next-line react-refresh/only-export-components
export const isNearBottom = (
	m: { scrollHeight: number; scrollTop: number; clientHeight: number },
	threshold = SCROLL_BOTTOM_THRESHOLD_PX,
) => m.scrollHeight - m.scrollTop - m.clientHeight <= threshold;

// The button is the only way back to the newest message once auto-follow is
// off, so a settled thread needs it just as much as a live one.
// eslint-disable-next-line react-refresh/only-export-components
export const deriveShowScrollButton = (isPinned: boolean) => !isPinned;

export const ChatMessageList = ({
	messages,
	chatId,
	currentPhase,
	onArtifactClick,
	topFade = false,
}: {
	messages: MessageWindowItem[];
	chatId: string;
	currentPhase?: string | null;
	onArtifactClick?: (
		panelType: string,
		itemId: string,
		artifactType?: string,
		strategyId?: string,
	) => void;
	// Pad + fade the top of the scroll box so content dissolves under the floating
	// header and the strategy dock bar. Only meaningful when that dock renders.
	topFade?: boolean;
}) => {
	// The campaign setup thread opens with the agent's own words above the campaign card. A
	// thread that arrives whole reads as a document, so the words are revealed as if spoken and
	// the card follows them, which is the order the bridge writes them in. Resolved here, once,
	// rather than inside the render map: a hook may not be called per message.
	const setupPanel = messages.find(
		(message) => message.type === 'process_setup_panel',
	);
	const setupProcessId =
		setupPanel && setupPanel.type === 'process_setup_panel'
			? setupPanel.process_id
			: null;
	const setupIntro = setupProcessId
		? messages.find((message) => message.id === `setup-intro-${setupProcessId}`)
		: undefined;
	const setupIntroText =
		setupIntro && setupIntro.type === 'agent_message' ? setupIntro.content : '';
	const revealedSetupIntro = useSetupIntroReveal(
		setupProcessId,
		setupIntroText,
		messages.length,
	);

	const contentContainerRef = useRef<HTMLDivElement>(null);
	const contentInnerRef = useRef<HTMLDivElement>(null);
	const isPinnedRef = useRef(true);
	const isProgrammaticScrollRef = useRef(false);
	const lastScrollTopRef = useRef(0);
	const lastClientHeightRef = useRef(0);
	const touchStartYRef = useRef(0);
	const prevLastUserMsgIdRef = useRef<string | null>(null);
	const prevChatIdRef = useRef(chatId);
	const [isPinned, setIsPinned] = useState(true);

	// Single source of truth for "is auto-follow on": the ref is read
	// synchronously by the auto-follow layout effect; the state drives the
	// jump-to-bottom button. They are always written together.
	const setPinned = useCallback((next: boolean) => {
		isPinnedRef.current = next;
		setIsPinned(next);
	}, []);

	// A box that cannot scroll has no position away from the bottom to hold, so
	// it must always read as pinned. Refusing to unpin one is only half of that
	// rule. Content also SHRINKS to fit, when a reasoning disclosure collapses or
	// a spinner goes, and that can happen while the reader is already unpinned.
	// Without this the jump-to-bottom button would sit over a thread with nothing
	// below it until she clicked it, sent a message or switched threads: no
	// scroll event can fire on a box that cannot scroll, so nothing else re-pins.
	// Returns true when it handled the box, so stopFollowing can stand down.
	const resumeIfUnscrollable = useCallback(() => {
		const el = contentContainerRef.current;
		if (!el || el.scrollHeight > el.clientHeight) return false;
		if (!isPinnedRef.current) setPinned(true);
		return true;
	}, [setPinned]);

	// Every route that stops auto-follow goes through here. A gesture on a box
	// that cannot scroll carries no intent to stop following, so it re-pins
	// instead of unpinning. Writing the ref synchronously, before the next
	// chunk's snap, wins the race with the stream.
	const stopFollowing = useCallback(() => {
		const el = contentContainerRef.current;
		if (!el || resumeIfUnscrollable()) return;
		if (isPinnedRef.current) setPinned(false);
	}, [resumeIfUnscrollable, setPinned]);

	const scrollToBottom = useCallback(() => {
		const el = contentContainerRef.current;
		if (!el) return;
		// A write that does not move the view fires no scroll event, so the flag
		// below would stay raised and swallow the user's next real scroll-up. This
		// is called on every size change, so that would happen often. Only write
		// when the view is off the bottom.
		if (
			isNearBottom(
				{
					scrollHeight: el.scrollHeight,
					scrollTop: el.scrollTop,
					clientHeight: el.clientHeight,
				},
				SCROLL_MAX_ROUNDING_PX,
			)
		) {
			lastScrollTopRef.current = el.scrollTop;
			return;
		}
		// Flag the resulting scroll event as self-induced so handleScroll ignores
		// it and can never re-pin the user off the back of our own snap.
		isProgrammaticScrollRef.current = true;
		el.scrollTop = el.scrollHeight;
		lastScrollTopRef.current = el.scrollTop;
	}, []);

	const bottommostMessage = messages[messages.length - 1];
	const bottommostContent =
		bottommostMessage?.type === 'agent_message'
			? bottommostMessage.content
			: bottommostMessage?.type === 'reasoning_step'
				? bottommostMessage.content
				: bottommostMessage?.type === 'tool_comment'
					? bottommostMessage.content
					: (bottommostMessage?.id ?? null);

	let lastUserMessageId: string | null = null;
	for (const m of messages) {
		if (m.type === 'user_message') lastUserMessageId = m.id;
	}

	const handleScroll = useCallback(() => {
		const el = contentContainerRef.current;
		if (!el) return;
		// Ignore the scroll event from our own auto-follow snap.
		if (isProgrammaticScrollRef.current) {
			isProgrammaticScrollRef.current = false;
			lastScrollTopRef.current = el.scrollTop;
			lastClientHeightRef.current = el.clientHeight;
			return;
		}
		const cur = el.scrollTop;
		// One snapshot feeds both bands, so they can never read different metrics.
		const metrics = {
			scrollHeight: el.scrollHeight,
			scrollTop: cur,
			clientHeight: el.clientHeight,
		};
		const atBottom = isNearBottom(metrics);
		// A clamp after a shrink always lands ON the new maximum. Only the rounding
		// slack separates that clamp from a real scroll-up. The 80 pixel band is far
		// too wide for that job. The scrollbar is visible now, and a drag on it fires
		// only a scroll event. A drag that stops inside the band would read as a
		// clamp, and it would never stop auto-follow.
		const atMax = isNearBottom(metrics, SCROLL_MAX_ROUNDING_PX);
		// The chat's open/grow transition animates clientHeight; as it grows the
		// browser clamps scrollTop downward, firing scroll events that are NOT the
		// user scrolling. Only treat scroll-position changes as intent when
		// clientHeight is stable. Streamed text grows scrollHeight (not
		// clientHeight), so genuine scroll-ups mid-stream are still detected.
		if (el.clientHeight === lastClientHeightRef.current) {
			if (cur < lastScrollTopRef.current) {
				// scrollTop fell. Two very different things do that, and `atMax` is what
				// separates them.
				if (atMax) {
					// Content SHRANK and the browser clamped scrollTop down to the new
					// maximum. The reader never moved, so auto-follow must survive.
					// `stopFollowing` is not enough on its own: it re-pins only a box that
					// became UNSCROLLABLE, so a shrink leaving a long thread still
					// scrollable — losing a spinner, collapsing a reasoning disclosure —
					// unpinned a reader who was sitting at the bottom. The unscrollable
					// rescue still has to run, because no scroll event can fire on a box
					// that cannot scroll, so nothing else would clear a stale jump button.
					resumeIfUnscrollable();
				} else {
					// The reader moved. A scroll-up can stop anywhere below the maximum,
					// and that includes a stop inside the 80 pixel band.
					stopFollowing();
				}
			} else if (cur > lastScrollTopRef.current && atBottom) {
				// Scrolled back down to the bottom — resume following. This branch keeps
				// the wider 80 pixel band on purpose, because resuming is safe anywhere
				// near the bottom. Never guarded: resuming is safe on any box.
				if (!isPinnedRef.current) setPinned(true);
			}
		}
		lastScrollTopRef.current = cur;
		lastClientHeightRef.current = el.clientHeight;
	}, [resumeIfUnscrollable, setPinned, stopFollowing]);

	const handleWheel = useCallback(
		(e: React.WheelEvent) => {
			// Genuine user intent: an upward wheel/trackpad gesture stops auto-follow.
			if (e.deltaY < 0) stopFollowing();
		},
		[stopFollowing],
	);

	const handleTouchStart = useCallback((e: React.TouchEvent) => {
		touchStartYRef.current = e.touches[0]?.clientY ?? 0;
	}, []);

	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			// On touch, dragging the finger downward reveals earlier content (scrolls
			// up). Treat that as the same stop-following intent as an upward wheel.
			const y = e.touches[0]?.clientY ?? 0;
			if (y - touchStartYRef.current > 8) stopFollowing();
		},
		[stopFollowing],
	);

	const handleJumpToBottom = useCallback(() => {
		setPinned(true);
		scrollToBottom();
	}, [setPinned, scrollToBottom]);

	// A thread switch does not remount this list, so the pin state of the thread
	// being left would otherwise survive into the thread being opened. Re-pin on
	// the chat id as well as on a new user message: a thread whose only messages
	// are the agent's has no last user message, so gating on that alone opened it
	// scrolled up, showing a jump button over a thread the reader never left.
	useLayoutEffect(() => {
		const switchedChat = prevChatIdRef.current !== chatId;
		if (switchedChat) prevChatIdRef.current = chatId;
		if (switchedChat || prevLastUserMsgIdRef.current !== lastUserMessageId) {
			prevLastUserMsgIdRef.current = lastUserMessageId;
			if (switchedChat || lastUserMessageId !== null) {
				setPinned(true);
				scrollToBottom();
			}
		}
	}, [chatId, lastUserMessageId, scrollToBottom, setPinned]);

	// Fallback beyond the bridge's 120s stream inactivity timeout
	const THINKING_TIMEOUT_MS = 180_000;
	const [thinkingTimedOut, setThinkingTimedOut] = useState(false);
	useEffect(() => {
		setThinkingTimedOut(false);
		if (
			currentPhase == null ||
			currentPhase === 'responding' ||
			currentPhase === 'finalizing'
		) {
			return;
		}
		const id = setTimeout(() => setThinkingTimedOut(true), THINKING_TIMEOUT_MS);
		return () => clearTimeout(id);
	}, [currentPhase]);

	const isThinking =
		!thinkingTimedOut &&
		currentPhase != null &&
		currentPhase !== 'responding' &&
		currentPhase !== 'finalizing' &&
		bottommostMessage?.type !== 'reasoning_step';

	useLayoutEffect(() => {
		if (isPinnedRef.current) {
			scrollToBottom();
		}
	}, [bottommostContent, isThinking, scrollToBottom]);

	// Re-pin as the list keeps growing after the render that mounted it. Reopening
	// a long thread jumps once while the chat box is still animating its height
	// and while uploaded photos are still zero-height, so the list is much shorter
	// at that moment than it becomes, and the view lands far above the newest
	// message. Observing the wrapper covers late image height too, because the
	// wrapper's own box grows when any child gains height. Reads the ref, not the
	// state, so the observer subscribes once and never re-registers.
	useEffect(() => {
		const box = contentContainerRef.current;
		const inner = contentInnerRef.current;
		const ro =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(() => {
						if (isPinnedRef.current) scrollToBottom();
						// A shrink with the reader already at the top needs no clamp, so
						// the browser fires no scroll event. This is the only witness.
						else resumeIfUnscrollable();
					})
				: null;
		if (box) ro?.observe(box);
		if (inner) ro?.observe(inner);
		return () => ro?.disconnect();
	}, [resumeIfUnscrollable, scrollToBottom]);

	const trailingToolComment =
		bottommostMessage?.type === 'agent_message' &&
		bottommostMessage.is_tool_comment === true
			? bottommostMessage
			: null;

	// A saved post offers "build a carousel" only while it is still the thing the
	// customer is looking at. Once they send another message the conversation has
	// moved on, and pressing it would drop an unrelated request into the middle
	// of a different thread. Same rule the task cards use for their actions.
	const lastUserMessageIndex = messages.reduce(
		(last, m, i) => (m.type === 'user_message' ? i : last),
		-1,
	);
	const staleArtifactIds = new Set(
		messages
			.filter((m, i) => m.type === 'panel_artifact' && i < lastUserMessageIndex)
			.map((m) => m.id),
	);

	const precedesToolComment = new Set<string>();
	for (let i = 0; i < messages.length - 1; i++) {
		const cur = messages[i];
		const next = messages[i + 1];
		if (
			cur.type === 'agent_message' &&
			cur.is_tool_comment !== true &&
			next.type === 'agent_message' &&
			next.is_tool_comment === true
		) {
			precedesToolComment.add(cur.id);
		}
	}

	const showScrollButton = deriveShowScrollButton(isPinned);

	// Both the panel_artifact card and an active process_setup_panel card open the
	// same detail panel on click, so they share one tracking + dispatch path.
	const emitArtifactClick = (
		panelType: string,
		itemId: string,
		artifactType: string | null,
		strategyId?: string,
	) => {
		trackEvent('Artifact Clicked', {
			panel_type: panelType,
			artifact_type: artifactType,
			artifact_id: itemId,
		});
		onArtifactClick?.(panelType, itemId, artifactType ?? undefined, strategyId);
	};

	return (
		<div className="relative flex min-h-0 w-full flex-1 flex-col">
			<div
				className={cn(
					// `overflow-x-hidden` is load-bearing, not tidying. Setting only
					// `overflow-y-auto` makes the other axis compute to `auto` rather than
					// `visible` (CSS Overflow 3). A message wider than this box could then
					// drag it sideways. `JobsWidget` and `AgentActivityFeed` clip the same
					// way in this same tree.
					`scrollbar-thin mx-auto flex w-full max-w-4xl flex-1 flex-col
					overflow-x-hidden overflow-y-auto px-6`,
					topFade ? 'pt-[var(--chat-top-fade)] pb-3' : 'pb-6',
				)}
				ref={contentContainerRef}
				onScroll={handleScroll}
				onWheel={handleWheel}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				data-testid="chat-scroll-container"
				style={
					topFade
						? ({
								// The header floats over this list; fade the top so text dissolves
								// as it scrolls under the controls (Grok-style) instead of clipping
								// at a hard edge. The mask is anchored to the scroll box (not the
								// scrolled content), so only the top band fades. 24px is the measured
								// distance from the scroll-box top to the strategy dock's BOTTOM tip:
								// content is fully faded out at/above that tip and dissolves in just
								// below it. The black stop (36px) is kept close to the dock bottom so
								// the feather is tight and doesn't bleed far past the component edge.
								// --chat-top-fade only drives pt, keeping the first message clear of
								// the fade at rest. All three track the dock's height — shrink the
								// dock's vertical padding and these come down by the same amount.
								'--chat-top-fade': '64px',
								WebkitMaskImage:
									'linear-gradient(to bottom, transparent 0, transparent 24px, black 36px)',
								maskImage:
									'linear-gradient(to bottom, transparent 0, transparent 24px, black 36px)',
							} as React.CSSProperties)
						: undefined
				}
			>
				{/* mt-auto bottom-anchors the thread: short conversations sit just
					above the input (Grok-style), and once content exceeds the viewport
					the auto margin collapses to 0 so it scrolls normally with the top
					reachable. */}
				<div
					ref={contentInnerRef}
					className={cn(
						'flex w-full flex-col items-center',
						topFade && 'mt-auto',
					)}
				>
					{(() => {
						// Agent actions only show on messages after the final user turn.
						// The flat render index is gone now that messages are grouped, so
						// flip this latch once that user turn has been emitted (reusing the
						// lastUserMessageId already derived above for scroll pinning).
						let afterLastUser = lastUserMessageId === null;
						const renderItems = groupMessages(messages);
						// A chat has at most one live reasoning run — streams are keyed per
						// chat, so turns are sequential, not concurrent. Only the LAST group
						// may show a spinner; an earlier group whose trailing step never got
						// marked done (abnormal stream end) must still render as settled.
						let latestReasoningGroupId: string | null = null;
						for (const item of renderItems) {
							if (item.kind === 'reasoning_group')
								latestReasoningGroupId = item.id;
						}
						return renderItems.map((item) => {
							if (item.kind === 'reasoning_group') {
								return (
									<ReasoningStepList
										key={item.id}
										steps={item.steps}
										isLatest={item.id === latestReasoningGroupId}
									/>
								);
							}
							if (item.kind === 'upload_group') {
								return (
									<UserUploadedImage
										key={item.id}
										imageUrls={item.images.map((image) => image.image_url)}
									/>
								);
							}

							if (item.kind === 'social_post_group') {
								// Staleness is uniform across the run: it groups only
								// strictly adjacent messages, so no customer turn can sit
								// inside one and every member falls on the same side of the
								// last user message.
								return (
									<MessageRow
										key={item.id}
										data-testid={`social-post-set-row-${item.id}`}
									>
										<SocialPostSet
											messages={item.messages}
											isFresh={!staleArtifactIds.has(item.messages[0].id)}
										/>
									</MessageRow>
								);
							}

							const message = item.message;
							if (
								message.type === 'user_message' &&
								message.id === lastUserMessageId
							) {
								afterLastUser = true;
							}

							// Emptying the content is how the bridge withdraws a message it
							// had already appended, so an empty one is not a message at all.
							if (
								(message.type === 'agent_message' ||
									message.type === 'tool_comment') &&
								message.content === ''
							) {
								return null;
							}
							if (precedesToolComment.has(message.id)) {
								return null;
							}
							if (
								message.type === 'agent_message' &&
								message.is_tool_comment === true
							) {
								if (message.id !== trailingToolComment?.id) return null;
								return (
									<Fragment key={message.id}>
										<ThinkingIndicator reasoningSummary={message.content} />
									</Fragment>
								);
							}

							return (
								<Fragment key={message.id}>
									{message.type === 'user_message' ? (
										<UserMessage message={message.content} />
									) : message.type === 'agent_message' ? (
										<AgentMessage
											message={
												revealedSetupIntro !== null &&
												message.id === `setup-intro-${setupProcessId}`
													? revealedSetupIntro
													: message.content
											}
											actions={message.actions}
											showActions={afterLastUser}
											isStreaming={message.is_streaming === true}
											tightMargins={topFade}
											redditPreview={message.reddit_preview}
										/>
									) : message.type === 'panel_artifact' ? (
										<PanelArtifactMessage
											message={message}
											canBuildCarousel={!staleArtifactIds.has(message.id)}
											onClick={() =>
												emitArtifactClick(
													message.panel_type,
													message.item_id,
													message.artifact_type ?? null,
													message.strategy_id,
												)
											}
										/>
									) : message.type === 'edited_image_artifact' ? (
										<EditedImageArtifact
											artifactId={message.artifact_id}
											actionImageUrl={message.image_url}
											imageIndex={message.image_index}
											originalImageUrl={message.original_image_url}
											wasCropped={message.was_cropped}
											via={message.via}
										/>
									) : message.type === 'removed_slide_artifact' ? (
										<RemovedSlideArtifact
											artifactId={message.artifact_id}
											removedImageUrl={message.removed_image_url}
											removedIndex={message.removed_index}
											priorContent={message.prior_content}
											resultImageUrls={message.result_image_urls}
										/>
									) : message.type === 'reference_image_request' ? (
										<ReferenceImageRequestCard
											message={message}
											chatId={chatId}
										/>
									) : message.type === 'instagram_connection_card' ? (
										<InstagramConnectionCard
											message={message}
											chatId={chatId}
										/>
									) : message.type === 'express_connection_card' ? (
										<ExpressConnectionCard message={message} chatId={chatId} />
									) : message.type === 'process_setup_panel' ? (
										// Held back while the words above are still being spoken, so the
										// campaign lands as their result rather than beside them.
										revealedSetupIntro !== null ? null : (
											<CampaignSetupPanel
												message={message}
												onClick={() =>
													emitArtifactClick('process', message.process_id, null)
												}
											/>
										)
									) : message.type === 'tool_comment' ? (
										<ToolCommentMessage content={message.content} />
									) : // A system_message is a model-facing role, and most carry
									// text no user should read: do_open_content_chat seeds one
									// that primes the chat agent, and start_content_chat
									// documents that text as invisible to the user. The stop
									// confirmation is the one that is written for the user, and
									// reason marks it. So test reason, never type alone.
									message.type === 'system_message' &&
									  message.reason === 'user_stopped' ? (
										<SystemMessage content={message.content} />
									) : null}
								</Fragment>
							);
						});
					})()}

					{isThinking && !trailingToolComment && (
						<ThinkingIndicator
							reasoningSummary={
								bottommostMessage?.type === 'agent_message'
									? bottommostMessage.reasoning_summary
									: undefined
							}
							fallback="Getting started…"
						/>
					)}

					{/* The vibe-edit wait happens after the agent turn ends, so isThinking
					is false and this is the only spinner. A live turn (isThinking) or a
					trailing tool-comment row already shows its own spinner, so suppress
					this one in those cases to avoid stacking two. */}
					{!isThinking && !trailingToolComment && (
						<EditInProgressIndicator chatId={chatId} />
					)}
				</div>
			</div>

			{showScrollButton && (
				<button
					type="button"
					onClick={handleJumpToBottom}
					aria-label="Scroll to bottom"
					className="absolute bottom-4 left-1/2 z-10 flex h-9 w-9
						-translate-x-1/2 items-center justify-center rounded-full border
						border-black/10 bg-white shadow-md transition-colors
						hover:bg-gray-50"
				>
					<ChevronDownIcon className="size-5 text-gray-700" />
				</button>
			)}
		</div>
	);
};
